import React, { useState, useEffect } from 'react';
import { useQuizContext } from '../../context/QuizContext';
import type { QuizItem } from '../../types/quiz';
import MultipleChoice from './types/multiple-choice/MultipleChoice';
import TrueFalse from './types/true-false/TrueFalse';
import Flashcard from './types/flashcard/Flashcard';
import FillInTheBlank from './types/fill-in-the-blank/FillInTheBlank';
import HotSpot from './types/hot-spot/HotSpot';
import OrderedResponse from './types/ordered-response/OrderedResponse';
import EnhancedHotSpot from './types/enhanced-hot-spot/EnhancedHotSpot';
import Cloze from './types/cloze/Cloze';
import SentenceDragAndDrop from './types/sentence-drag-and-drop/SentenceDragAndDrop';
import Matrix from './types/matrix/Matrix';
import MultipleCheckbox from './types/multiple-checkbox/MultipleCheckbox';
import LabelMatching from './types/label-matching/LabelMatching';
import Graphic from './types/graphic/Graphic';
import DragAndDrop from './types/drag-and-drop/DragAndDrop';
import { Box } from '@osmosis/react-ui/components/Box';
import { Flex } from '@osmosis/react-ui/components/Box';
import RateQuestion from './RateQuestion';
import useHtmlImageViewer from '../../hooks/useHtmlImageViewer';
import useModal from '../../hooks/useModal';
import ImageViewer from '../ImageViewer';
import { QuizType } from '@osmosis/shared/types/quiz';
import Modal from '../Modal';
import LockoutModalContent from '../LockoutModalContent';
import { useUserContext } from '../../context/UserContext';
import ExpiredUserLockoutModal from '../learn/ExpiredUserModal';
import { ExpiredUserModalType } from '../../constants/ExpiredUserModalConfig';
import { FormattedMessage, useIntl } from 'react-intl';
import { LockoutModalVariant } from '../../constants/lockout';
import { P } from '@osmosis/react-ui/components/Text';
import { bg1 } from '@osmosis/tailwind-ui/styles';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faClose } from '@fortawesome/pro-light-svg-icons';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { Button } from '@osmosis/react-ui/components/Button';
const QUIZ_CONTAINER_ID = 'quiz-container';

const QuizItem = ({
  item,
  queueType,
}: {
  item: QuizItem;
  queueType?: string;
}) => {
  switch (item?.subtype) {
    case QuizType.MultipleChoice:
    case QuizType.Exhibit:
      return <MultipleChoice />;
    case QuizType.TrueFalse:
      return <TrueFalse />;
    case QuizType.ExtendedMultipleCheckbox:
    case QuizType.MultipleCheckbox:
      return <MultipleCheckbox />;
    case QuizType.LabelMatching:
      return <LabelMatching />;
    case QuizType.Flashcard:
      return <Flashcard queueType={queueType} />;
    case QuizType.FillInTheBlankRN:
      return <FillInTheBlank />;
    case QuizType.HotSpot:
      return <HotSpot />;
    case QuizType.OrderedResponse:
      return <OrderedResponse />;
    case QuizType.EnhancedHotSpot:
      return <EnhancedHotSpot />;
    case QuizType.Cloze:
      return <Cloze />;
    case QuizType.SentenceDragAndDrop:
      return <SentenceDragAndDrop />;
    case QuizType.Matrix:
      return <Matrix />;
    case QuizType.Graphic:
      return <Graphic />;
    case QuizType.OrderedDragAndDrop:
    case QuizType.ExtendedDragAndDrop:
      return <DragAndDrop />;
    default:
      return null;
  }
};

type Props = {
  onCloseLockoutModal: () => void;
  queueType?: string;
};
/**
 * Renders the correct quiz type based on the current item.
 */
const QuizItemContainer = ({ queueType, onCloseLockoutModal }: Props) => {
  const { currentItem, quiz, currentIndex, changeItem } = useQuizContext();
  const [image, setImage] = useState<string>();
  const [loginPopup, setLoginPopup] = useState<boolean>(false);
  const [expiredPopup, setExpiredPopup] = useState<boolean>(false);
  const { user, hasPrime } = useUserContext();
  const { highlighting } = useQuizContext();
  const colors = useColors();
  const intl = useIntl();
  const closeHighlightInstructionLabel = intl.formatMessage({
    id: 'quiz.closeHighlightInstructionAriaLabel',
    defaultMessage: 'Close highlight instruction',
  });
  const [showHighlightInstruction, setShowHighlightInstruction] =
    useState(false);
  useEffect(() => {
    if (highlighting) {
      setShowHighlightInstruction(true);
    } else {
      setShowHighlightInstruction(false);
    }
  }, [highlighting]);

  const imageViewer = useModal();

  const handleClickImage = (src: string) => {
    setImage(src);
    imageViewer.show();
  };

  useHtmlImageViewer(QUIZ_CONTAINER_ID, handleClickImage);

  useEffect(() => {
    const isGuest = !user;
    const isQuizLockedForGuest = currentIndex >= 2 && isGuest;
    const loggedOutCount = quiz.filter(
      (item) => item?.item?.data?.loggedOut === 'true',
    ).length;

    if (isGuest && (isQuizLockedForGuest || loggedOutCount < 2)) {
      setLoginPopup(true);
    } else if (user && !hasPrime && currentIndex >= 2) {
      setExpiredPopup(true);
    }
  }, [currentIndex, user, quiz]);

  const handleCloseModal = () => {
    onCloseLockoutModal();
    setLoginPopup(false);
    setExpiredPopup(false);
    if (currentIndex >= 2) {
      changeItem(-1);
    }
  };

  // For accessibility reasons, each image which is clickable
  // should have a tabIndex and an aria-label to indicate
  // to screen readers or keyboard users that it is interactable.
  // Since the image data is hard-coded in questions, we manually
  // insert these attributes here.
  const label = useIntl().formatMessage({
    id: 'quiz.imagePressToEnlargeAriaLabel',
    defaultMessage: 'Press Enter or Space to expand the image',
  });
  if (currentItem?.data && 'question' in currentItem.data) {
    currentItem.data.question = currentItem.data.question.replace(
      /<img /g,
      `<img tabindex="0" aria-label="${label}" data-testid="lightbox-image" `,
    );
  }

  return (
    <>
      <Modal isVisible={imageViewer.isVisible} hide={imageViewer.toggle}>
        <ImageViewer onClose={imageViewer.hide} src={image} />
      </Modal>

      {loginPopup && (
        <Modal
          role="dialog"
          isVisible={loginPopup}
          hide={() => false}
          overlayOpacity={0.2}
          _className="!overflow-visible !max-h-[85vh] !max-w-[600px] !w-full"
        >
          <LockoutModalContent
            dataTestId="quiz-lockout-modal"
            onClose={handleCloseModal}
            closeButton={true}
            variant={LockoutModalVariant.QUIZ}
          />
        </Modal>
      )}

      {expiredPopup && (
        <ExpiredUserLockoutModal
          modalType={ExpiredUserModalType.QUIZ}
          isVisible={expiredPopup}
          hide={handleCloseModal}
        />
      )}

      <Box data-id={QUIZ_CONTAINER_ID} flex={1} minHeight={540}>
        <Box>
          {showHighlightInstruction && (
            <>
              <Box aria-live="polite" role="alert" className="sr-only">
                <FormattedMessage
                  id="quiz.imageClickToEnlargeAriaLabel"
                  defaultMessage="Keyboard instructions: After focusing the text, use shift and left or right arrows to select,
                                    and press H to highlight."
                />
              </Box>
              <Box
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
                aria-hidden="true"
                onFocus={() => {
                  document.getElementById('highlight-instruction')?.focus();
                }}
                style={{ outline: 'none' }}
              />

              <Flex
                id="highlight-instruction"
                className={`m-1 ${bg1} rounded-md px-4 py-2`}
                mb={4}
                tabIndex={-1}
              >
                <P>
                  <b>
                    <FormattedMessage
                      id="quiz.highlightInstruction"
                      defaultMessage="Keyboard instructions: "
                    />
                  </b>
                  <FormattedMessage
                    id="quiz.imageClickToEnlargeAriaLabel"
                    defaultMessage="After focusing the text, use shift and left or right arrows to select,
            and press H to highlight."
                  />
                </P>

                <Button
                  aria-label={closeHighlightInstructionLabel}
                  data-testid="quiz.highlightInstruction.closeBtn"
                  className="ml-auto flex h-6 w-6 items-center justify-center rounded bg-none p-2"
                  onClick={() => setShowHighlightInstruction(false)}
                  bg="transparent"
                >
                  <Icon
                    data-testid="quiz-highlight-instruction-close-icon"
                    icon={faClose}
                    color={colors.gray70}
                    size={20}
                  />
                </Button>
              </Flex>
            </>
          )}
        </Box>

        {/* Key required here to re-render new questions`` */}
        {currentItem && (
          <QuizItem
            item={currentItem}
            key={currentItem?.nodeID}
            queueType={queueType}
          />
        )}
        {/* not showing Rate Question for flashcard queue*/}
        {queueType !== 'flashcards' && user && (
          <Flex justifyContent="center" mt={3}>
            <RateQuestion />
          </Flex>
        )}
      </Box>
    </>
  );
};

export default QuizItemContainer;
